import React, { createContext, useContext, useEffect, useState } from 'react';
import { getSession, getUserFromSession, CognitoAuthUser } from '../services/cognitoAuth';
import { debug } from '../utils/debug';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface AuthContextType {
  isAdmin: boolean;
  userGroups: string[];
  userEmail: string | null;
  isLoading: boolean;
  role: string;
  checkAdminAccess: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
  user?: CognitoAuthUser | any;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, user }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const tokens = await getSession();
        const authUser = getUserFromSession(tokens);

        const email = authUser.email || user?.email || null;
        setUserEmail(email);

        // DB is the source of truth for authorization (post account-split,
        // Cognito groups are no longer used). Fall back to JWT groups only
        // if the API is unreachable, so admins are never silently demoted.
        let hasAdminAccess = false;
        let groups: string[] = authUser.groups || [];
        try {
          const resp = await fetch(`${API_BASE_URL}/api/user/profile`, {
            headers: { Authorization: `Bearer ${tokens.idToken}` },
          });
          if (resp.ok) {
            const profile = await resp.json();
            hasAdminAccess = Boolean(profile.isAdmin) || profile.role === 'ADMIN';
            if (profile.role) groups = [profile.role];
          } else {
            debug.warn('Profile fetch failed:', resp.status);
          }
        } catch (e) {
          debug.warn('Profile fetch threw, falling back to JWT groups:', e);
          hasAdminAccess = groups.some(group =>
            ['admin', 'admins', 'administrator', 'administrators'].includes(group.toLowerCase()),
          );
        }

        setUserGroups(groups);
        setIsAdmin(hasAdminAccess);

        debug.log('User role/groups:', groups);
        debug.log('Is admin:', hasAdminAccess);
        debug.log('User email:', email);
      } catch (error) {
        debug.error('Error checking user role:', error);
        setIsAdmin(false);
        setUserGroups([]);
      } finally {
        setIsLoading(false);
      }
    };

    checkUserRole();
  }, [user]);

  const checkAdminAccess = () => {
    if (!isAdmin) {
      debug.warn('Access denied: Admin privileges required');
      return false;
    }
    return true;
  };

  const role = userGroups.length > 0 ? userGroups[0].toUpperCase() : 'REGULAR';

  return (
    <AuthContext.Provider value={{
      isAdmin,
      userGroups,
      userEmail,
      isLoading,
      role,
      checkAdminAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
